/**
 * Consolidated API Client
 * Handles HTTP requests, response parsing, and error normalization
 * UI concerns (toasts, etc.) are handled by hooks, not this client
 */

import type { DataResponse, MessageResponse, ErrorResponse } from '@zlinkx/shared-v2';
import type { ApiError, HttpMethod, ApiClientOptions, ApiClientConfig } from '@/types/infrastructure.types';
import { captureStructuredLog, OBSERVABILITY_EVENTS } from '@/lib/observability';

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

// Configuration
const rawApiBaseUrl: unknown = import.meta.env.VITE_API_URL;
const API_BASE_URL =
	typeof rawApiBaseUrl === 'string' && rawApiBaseUrl.length > 0 ? rawApiBaseUrl : 'http://localhost:55';
const LOGIN_URL = '/merchant/login'; // Configurable login redirect

// Import Supabase for getting access token (Story 0-6)
// Story 2-8: tokenManager removed - Supabase is now the only auth method
import { supabase } from '@/lib/supabase';

// Debug logging (development only)
if (import.meta.env.DEV) {
	console.log('[API Client] Base URL:', API_BASE_URL);
	console.log('[API Client] Mode:', import.meta.env.MODE);
}

class ApiClient {
	private baseURL: string;
	private defaultHeaders: Record<string, string>;
	private abortControllers: Map<string, AbortController>;
	private readonly MAX_ABORT_CONTROLLERS = 100;
	private loginUrl: string;

	constructor(
		baseURL: string = API_BASE_URL,
		options?: {
			loginUrl?: string;
		},
	) {
		this.baseURL = baseURL;
		this.defaultHeaders = {
			'Content-Type': 'application/json',
		};
		this.abortControllers = new Map();
		this.loginUrl = options?.loginUrl ?? LOGIN_URL;
	}

	/**
	 * Core request method that handles all HTTP operations
	 * Returns parsed data directly (not wrapped in response object)
	 */
	private async request<T>(method: HttpMethod, url: string, data?: unknown, options?: ApiClientOptions): Promise<T> {
		// Handle request cancellation
		const requestId = options?.requestId ?? `${method}-${url}`;
		if (this.abortControllers.has(requestId)) {
			this.abortControllers.get(requestId)?.abort();
		}

		const abortController = new AbortController();
		this.abortControllers.set(requestId, abortController);

		// Prevent memory leak - clean up old controllers if too many
		if (this.abortControllers.size > this.MAX_ABORT_CONTROLLERS) {
			const firstKeyResult = this.abortControllers.keys().next();
			if (firstKeyResult.done === false) {
				this.abortControllers.delete(firstKeyResult.value);
			}
		}

		// Build headers with auth token from Supabase session (Story 0-6)
		const token = await this.getAuthToken();
		const headers = {
			...this.defaultHeaders,
			...(token !== null ? { Authorization: `Bearer ${token}` } : {}),
			...(options?.headers || {}),
		};

		// Build request config
		const config: RequestInit = {
			method,
			headers,
			signal: options?.signal || abortController.signal,
			...(data !== undefined && data !== null ? { body: JSON.stringify(data) } : {}),
		};

		// Build URL with query params
		let finalUrl = `${this.baseURL}${url}`;
		if (options?.params) {
			const queryString = this.buildQueryString(options.params);
			finalUrl += `?${queryString}`;
		}

		try {
			const response = await fetch(finalUrl, config);

			// Parse response
			const responseData = await this.parseResponse<T>(response, { method, url: finalUrl });

			// Clean up abort controller
			this.abortControllers.delete(requestId);

			return responseData;
		} catch (error) {
			// Clean up abort controller
			this.abortControllers.delete(requestId);

			// Handle cancellation
			if (error instanceof Error && error.name === 'AbortError') {
				throw this.createApiError('Request cancelled', 'REQUEST_CANCELLED', undefined, {
					method,
					url: finalUrl,
				});
			}

			// Re-throw if already an ApiError
			if (this.isApiError(error)) {
				throw error;
			}

			// Convert to ApiError
			captureStructuredLog(OBSERVABILITY_EVENTS.LOG_API_REQUEST_FAILED, {
				status: null,
				error_code: 'NETWORK_ERROR',
				method,
				endpoint: this.getPathname(finalUrl),
				message: error instanceof Error ? error.message : 'An unexpected error occurred',
			}, {
				dedupeKey: `network:${method}:${this.getPathname(finalUrl)}`,
				level: 'warning',
			});

			throw this.createApiError(
				error instanceof Error ? error.message : 'An unexpected error occurred',
				'NETWORK_ERROR',
				undefined,
				{ method, url: finalUrl },
			);
		}
	}

	/**
	 * Parse response and extract data or throw normalized error
	 */
	private async parseResponse<T>(response: Response, request: { method: string; url: string }): Promise<T> {
		let responseData: unknown;

		try {
			responseData = await response.json();
		} catch {
			if (!response.ok) {
				// Handle 401 without JSON body
				if (response.status === 401) {
					this.captureHttpFailure(response, request, 'UNAUTHORIZED');
					await this.handleUnauthorized(response, request);
				} else {
					this.captureHttpFailure(response, request, `HTTP_${response.status}`);
				}
				throw this.createApiError(
					`HTTP ${response.status}: ${response.statusText}`,
					`HTTP_${response.status}`,
					response.status,
					request,
				);
			}
			throw this.createApiError('Invalid JSON response', 'PARSE_ERROR', undefined, request);
		}

		// Handle 401 Unauthorized FIRST - regardless of response format
		// This catches "User profile not found" errors from users who exist in
		// Supabase auth but don't have a profile row in the users table
		if (response.status === 401) {
			const responseCode = this.isErrorResponse(responseData) ? responseData.error.code : 'UNAUTHORIZED';
			this.captureHttpFailure(response, request, responseCode);
			await this.handleUnauthorized(response, request);
			const error = this.isErrorResponse(responseData)
				? this.createApiErrorFromResponse(responseData, response.status, request)
				: this.createApiError('Session expired', 'UNAUTHORIZED', 401, request);
			throw error;
		}

		// Handle our standardized API format
		if (this.isErrorResponse(responseData)) {
			if (!response.ok) {
				this.captureHttpFailure(response, request, responseData.error.code);
			}
			throw this.createApiErrorFromResponse(responseData, response.status, request);
		}

		// Return full response objects (not just data/message)
		if (this.isDataResponse<T>(responseData) || this.isMessageResponse(responseData)) {
			return responseData as T;
		}

		// Note: 401 handling is done earlier in this method (before ErrorResponse check)

		// Non-standard response format
		if (!response.ok) {
			this.captureHttpFailure(response, request, `HTTP_${response.status}`);
			throw this.createApiError(
				`Request failed with status ${response.status}`,
				`HTTP_${response.status}`,
				response.status,
				request,
			);
		}

		// Return as-is for non-standard success responses
		return responseData as T;
	}

	/**
	 * Create normalized ApiError from various sources
	 */
	private createApiError(
		message: string,
		code?: string,
		status?: number,
		request?: { method: string; url: string },
	): ApiError {
		const error = new Error(message) as ApiError;
		error.name = 'ApiError';
		error.code = code;
		error.status = status;
		error.request = request;
		return error;
	}

	/**
	 * Create ApiError from error response
	 */
	private createApiErrorFromResponse(
		response: ErrorResponse,
		status: number,
		request?: { method: string; url: string },
	): ApiError {
		// ErrorResponse has nested structure: { error: { code, message, details? } }
		const error = this.createApiError(
			response.error.message || 'An error occurred',
			response.error.code,
			status,
			request,
		);

		// Extract field errors from details if present
		if (response.error.details && Array.isArray(response.error.details)) {
			// Convert array of { path, message } to field errors object
			const fieldErrors: Record<string, string> = {};
				response.error.details.forEach(detail => {
					if (Array.isArray(detail.path) && detail.path.length > 0) {
						const fieldName = detail.path.join('.');
						fieldErrors[fieldName] = detail.message;
					}
				});
			if (Object.keys(fieldErrors).length > 0) {
				error.fieldErrors = fieldErrors;
			}
		}

		error.details = response.error.details;
		return error;
	}

	/**
	 * Type guards
	 */
	private isErrorResponse(data: unknown): data is ErrorResponse {
		return isRecord(data) && 'success' in data && data.success === false;
	}

	private isDataResponse<T>(data: unknown): data is DataResponse<T> {
		return isRecord(data) && 'success' in data && data.success === true && 'data' in data;
	}

	private isMessageResponse(data: unknown): data is MessageResponse {
		return isRecord(data) && 'success' in data && data.success === true && 'message' in data && !('data' in data);
	}

	private isApiError(error: unknown): error is ApiError {
		return error instanceof Error && 'name' in error && error.name === 'ApiError';
	}

	private isFieldErrorObject(obj: unknown): obj is Record<string, string> {
		if (!isRecord(obj)) return false;

		return Object.values(obj).every(value => typeof value === 'string');
	}

	private getPathname(url: string): string {
		try {
			return new URL(url).pathname;
		} catch {
			return url;
		}
	}

	private getServerRequestId(response: Response): string | null {
		const requestId = response.headers.get('x-request-id') || response.headers.get('X-Request-ID');
		return requestId && requestId.length > 0 ? requestId : null;
	}

	private captureHttpFailure(
		response: Response,
		request: { method: string; url: string },
		errorCode: string,
	): void {
		const endpoint = this.getPathname(request.url);
		const requestId = this.getServerRequestId(response);
		const status = response.status;
		const eventName =
			status === 401 ? OBSERVABILITY_EVENTS.LOG_API_401_RECEIVED : OBSERVABILITY_EVENTS.LOG_API_REQUEST_FAILED;

		captureStructuredLog(eventName, {
			status,
			error_code: errorCode,
			method: request.method,
			endpoint,
			request_id: requestId,
		}, {
			dedupeKey: `${eventName}:${request.method}:${endpoint}:${errorCode}:${status}`,
			level: status >= 500 ? 'error' : 'warning',
		});
	}

	/**
	 * Utility methods
	 */
	private buildQueryString(
		params: Record<string, string | number | boolean | (string | number | boolean)[] | undefined | null>,
	): string {
		const searchParams = new URLSearchParams();

		Object.entries(params).forEach(([key, value]) => {
			// Skip undefined or null values - they should not be included in query string
			if (value === undefined || value === null) {
				return;
			}

			if (Array.isArray(value)) {
				// For arrays, append each value with the same key
				// This creates ?key=value1&key=value2&key=value3
				value.forEach(item => {
					if (item !== undefined && item !== null) {
						searchParams.append(key, String(item));
					}
				});
			} else {
				searchParams.append(key, String(value));
			}
		});

		return searchParams.toString();
	}

	/**
	 * Get auth token from Supabase session
	 * Story 2-8: Supabase is now the only auth method (tokenManager removed)
	 */
	private async getAuthToken(): Promise<string | null> {
		try {
			const result = await supabase.auth.getSession();
			return result?.data?.session?.access_token ?? null;
		} catch (error) {
			console.error('[API Client] Error getting auth token:', error);
			return null;
		}
	}

	private async clearAuthTokens(): Promise<void> {
		try {
			await supabase.auth.signOut();
		} catch (error) {
			console.error('[API Client] Error signing out from Supabase:', error);
		}
	}

	/**
	 * Handle 401 Unauthorized responses
	 * Clears auth state and redirects to login
	 *
	 * This handles cases like:
	 * - Expired/invalid tokens
	 * - User exists in Supabase auth but has no profile in users table
	 *   (missing database trigger or manual auth user creation)
	 */
	private async handleUnauthorized(response?: Response, request?: { method: string; url: string }): Promise<void> {
		const endpoint = request ? this.getPathname(request.url) : undefined;
		const requestId = response ? this.getServerRequestId(response) : undefined;
		captureStructuredLog(OBSERVABILITY_EVENTS.LOG_FORCED_LOGOUT_TRIGGERED, {
			reason: 'api_401',
			status: 401,
			method: request?.method,
			endpoint,
			request_id: requestId ?? null,
		}, {
			dedupeKey: `forced_logout:${request?.method ?? ''}:${endpoint ?? ''}:${requestId ?? ''}`,
			level: 'warning',
		});

		console.warn('[API Client] Unauthorized - clearing session and redirecting to login');
		await this.clearAuthTokens();
		window.location.href = this.loginUrl;
	}

	/**
	 * Public HTTP methods with enforced response types
	 * GET/POST return DataResponse<T>
	 * PUT/PATCH/DELETE return MessageResponse
	 */
	async get<T>(url: string, options?: ApiClientOptions): Promise<DataResponse<T>> {
		return this.request<DataResponse<T>>('GET', url, undefined, options);
	}

	async post<T>(url: string, data?: unknown, options?: ApiClientOptions): Promise<DataResponse<T>> {
		return this.request<DataResponse<T>>('POST', url, data, options);
	}

	async put(url: string, data?: unknown, options?: ApiClientOptions): Promise<MessageResponse> {
		return this.request<MessageResponse>('PUT', url, data, options);
	}

	async patch(url: string, data?: unknown, options?: ApiClientOptions): Promise<MessageResponse> {
		return this.request<MessageResponse>('PATCH', url, data, options);
	}

	async delete(url: string, options?: ApiClientOptions): Promise<MessageResponse> {
		return this.request<MessageResponse>('DELETE', url, undefined, options);
	}

	/**
	 * Cancel a specific request
	 */
	cancelRequest(requestId: string): void {
		const controller = this.abortControllers.get(requestId);
		if (controller) {
			controller.abort();
			this.abortControllers.delete(requestId);
		}
	}

	/**
	 * Cancel all pending requests
	 */
	cancelAllRequests(): void {
		this.abortControllers.forEach(controller => controller.abort());
		this.abortControllers.clear();
	}
}

// ============================================================================
// API CLIENT EXPORTS AND CONFIGURATION
// ============================================================================

/**
 * DEFAULT API CLIENT - Use this for 99% of cases
 *
 * This singleton instance is pre-configured with:
 * - Base URL from VITE_API_URL environment variable
 * - Standard auth token management (accessToken/refreshToken in localStorage)
 * - Automatic redirect to /merchant/login on 401 errors
 *
 * Usage:
 * ```typescript
 * import { apiClient } from './api-client';
 *
 * const userResponse = await apiClient.get<UserProfile>('/api/user/profile');
 * const userData = userResponse.data;
 * ```
 */
export const apiClient = new ApiClient();

/**
 * ADVANCED: Configuration options for custom API clients
 *
 * Only needed when you have:
 * - Multiple backend services (microservices)
 * - Different authentication systems
 * - Third-party API integrations
 * - Regional API endpoints
 *
 * DO NOT USE during initial development - stick with the default apiClient above
 */

/**
 * ADVANCED: Factory function for creating custom API client instances
 *
 * Only use when you need multiple API clients in the same app:
 *
 * Examples of when you MIGHT need this (unlikely in early development):
 *
 * 1. Microservices architecture:
 *    ```typescript
 *    const mainApi = createApiClient({ baseURL: 'https://api.zlinkx.com' });
 *    const analyticsApi = createApiClient({ baseURL: 'https://analytics.zlinkx.com' });
 *    ```
 *
 * 2. Different auth systems:
 *    ```typescript
 *    const publicApi = createApiClient({ baseURL: '/public', loginUrl: '' });
 *    const adminApi = createApiClient({ baseURL: '/admin', loginUrl: '/admin/login' });
 *    ```
 *
 * 3. Third-party APIs:
 *    ```typescript
 *    const stripeApi = createApiClient({ baseURL: 'https://api.stripe.com/v1' });
 *    ```
 *
 * 4. Testing with mock servers:
 *    ```typescript
 *    const mockApi = createApiClient({ baseURL: 'http://localhost:3001/mock' });
 *    ```
 *
 * FOR INITIAL DEVELOPMENT: Ignore this function and use the default `apiClient` export
 */
export function createApiClient(config?: ApiClientConfig): ApiClient {
	return new ApiClient(config?.baseURL, {
		loginUrl: config?.loginUrl,
	});
}
